import React, { createRef, RefObject, useRef } from 'react';

import { ToastItem } from '~/components/toasts-layout/ToastItem';
import type { DisplayToast } from '~/components/toasts-layout/ToastItem';
import * as S from '~/components/toasts-layout/ToastsLayout.styled';
import { useToasts } from '~/stores/use-toasts/useToasts';
import { CONVERSATION_TOAST } from '~/views/alfred/research/use-notification-toast/useNotificationToast';

const SCOPES_TO_CLEAR = [CONVERSATION_TOAST];

const { offsetBetweenToasts } = S;

export const ToastsLayout = () => {
  const { toasts, clearScopedToasts } = useToasts(state => ({
    toasts: state.toasts,
    clearScopedToasts: state.clearScopedToasts,
  }));

  const refs = useRef<Record<string, RefObject<HTMLDivElement | null>>>({});

  toasts.forEach(t => {
    refs.current[t.id] = refs.current[t.id] || createRef();
  });

  const toastIds = new Set(toasts.map(t => String(t.id)));
  Object.keys(refs.current).forEach(id => {
    if (!toastIds.has(id)) {
      delete refs.current[id];
    }
  });

  const displayToasts: DisplayToast[] = toasts.map((toast, i) => ({
    toast,
    exiting: !!toast.exiting,
    displayIndex: i,
  }));

  const yOffsets = displayToasts.map((_dt, toastIndex) =>
    displayToasts.slice(0, toastIndex).reduce((offsetY, dt) => {
      const ref = refs.current[dt.toast.id];
      const curr = displayToasts[toastIndex];
      if (ref?.current && curr && dt.toast.position === curr.toast.position) {
        return offsetY + ref.current.clientHeight + offsetBetweenToasts;
      }
      return offsetY;
    }, 0)
  );

  const filteredScopes = SCOPES_TO_CLEAR.filter(scope =>
    toasts.some(toast => toast.scope === scope)
  );

  const handleScopedToastsClose = () => {
    filteredScopes.forEach(scope => clearScopedToasts(scope));
  };

  return (
    <S.ToastsWrapper
      data-testid="toasts-wrapper"
      onClick={
        filteredScopes.length ? () => handleScopedToastsClose() : undefined
      }
    >
      {displayToasts.map((displayToast, toastIndex) => (
        <ToastItem
          key={displayToast.toast.id}
          ref={refs.current[displayToast.toast.id]}
          displayToast={displayToast}
          $offsetY={yOffsets[toastIndex]}
        />
      ))}
    </S.ToastsWrapper>
  );
};
